import React from 'react';
import { EyeOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { truncateAddress } from '@/utils/address';
import { SupportedChainId } from '@packet/constants/AppSupportChains';
import { useNavigate } from 'react-router-dom';
import AssetAmount from '@/components/AssetView/AssetAmount';
import AssetSymbol from '@/components/AssetView/AssetSymbol';
import { Hash } from 'viem';
import { CommitmentStatus } from '@/components/CommitmentStatus';
import { CommitmentBaseDto } from '@/types/commitment';

// UTXO项接口
export interface UTXOItem extends CommitmentBaseDto {
  id: string;
  amount: string;
  asset: Hash;
  chainId: SupportedChainId;
}

// UTXO组件的Props接口
export interface UTXOProps {
  inputs: UTXOItem[];
  outputs: UTXOItem[];
}

export default function UTXO({ inputs, outputs }: UTXOProps) {
  console.log('UTXO inputs:', inputs);
  console.log('UTXO outputs:', outputs);
  const navigate = useNavigate();
  const renderUTXOTable = (title: string, items: UTXOItem[]) => (
    <UTXOSection>
      <UTXOTable>
        {/* 表头 */}
        <TableHeader>
          <HeaderCell width="140px">{title}</HeaderCell>
          <HeaderCell flex="1">Amount</HeaderCell>
          <HeaderCell flex="0">State</HeaderCell>
          <HeaderCell flex="0" align="right">
            Operate
          </HeaderCell>
        </TableHeader>

        {/* 表格内容 */}
        {items.map((item, index) => (
          <TableRow
            key={item.id}
            $isLast={index === items.length - 1}
            onClick={() => {
              navigate(`/utxo/${item.chainId}/${item.id}`);
            }}
          >
            <DataCell width="140px">
              <MobileLabel>{title}</MobileLabel>
              <UTXOId>{truncateAddress(item.id, 6, 4)}</UTXOId>
            </DataCell>
            <DataCell flex="1">
              <MobileLabel>Amount</MobileLabel>
              <AssetAmount assetId={item.asset} chainId={item.chainId} amount={item.amount} /> <AssetSymbol assetId={item.asset} chainId={item.chainId} />
            </DataCell>
            <DataCell flex="0">
              <MobileLabel>State</MobileLabel>
              <CommitmentStatus is_frozen={item.is_frozen} is_spent={item.is_spent} />
            </DataCell>
            <DataCell flex="0" align="right">
              <MobileLabel>Operate</MobileLabel>
              <OperateButtons>
                <OperateButton>
                  <EyeOutlined />
                </OperateButton>
              </OperateButtons>
            </DataCell>
          </TableRow>
        ))}
      </UTXOTable>
    </UTXOSection>
  );

  return (
    <UTXOContainer>
      <SectionTitle>
        <TitleIndicator />
        <SectionTitleText>UTXO</SectionTitleText>
      </SectionTitle>

      <UTXOContent>
        {/* Input表格 */}
        <UTXOTableContainer>{renderUTXOTable('Input', inputs)}</UTXOTableContainer>

        {/* Output表格 */}
        <UTXOTableContainer>{renderUTXOTable('Output', outputs)}</UTXOTableContainer>
      </UTXOContent>
    </UTXOContainer>
  );
}

// Styled Components
const UTXOContainer = styled.div`
  width: 100%;
  margin-top: 48px;

  @media (max-width: 768px) {
    margin-top: 32px;
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`;

const TitleIndicator = styled.div`
  width: 2px;
  height: 16px;
  background-color: #141413;
`;

const SectionTitleText = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #141413;
  margin: 0;
`;

const UTXOContent = styled.div`
  display: flex;
  gap: 24px;
  width: 100%;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 32px;
  }

  @media (max-width: 768px) {
    gap: 24px;
  }
`;

const UTXOTableContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const UTXOSection = styled.div`
  width: 100%;
`;

const UTXOTable = styled.div`
  width: 100%;
  background: #ffffff;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  height: 56px;
  border-bottom: 1px solid rgba(16, 41, 0, 0.1);
  background: #ffffff;

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderCell = styled.div<{
  width?: string;
  flex?: string;
  align?: string;
}>`
  ${(props) => props.width && `width: ${props.width};`}
  ${(props) => props.flex && `flex: ${props.flex};`}
  padding: 10px 0;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #141413;
  text-align: ${(props) => props.align || 'left'};
  line-height: 1;

  &:first-child {
    padding-left: 0;
    padding-right: 24px;
  }

  &:not(:first-child):not(:last-child) {
    padding-left: 32px;
    padding-right: 32px;
  }

  &:last-child {
    padding-left: 32px;
    padding-right: 0;
  }
`;

const TableRow = styled.div<{ $isLast: boolean }>`
  display: flex;
  align-items: center;
  min-height: 56px;
  background: #ffffff;
  border-bottom: ${(props) => (props.$isLast ? 'none' : '1px solid rgba(16, 41, 0, 0.1)')};
  cursor: pointer;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px 0;
    gap: 16px;
    border-bottom: 1px solid rgba(16, 41, 0, 0.1);
  }
`;

const DataCell = styled.div<{ width?: string; flex?: string; align?: string }>`
  ${(props) => props.width && `width: ${props.width};`}
  ${(props) => props.flex && `flex: ${props.flex};`}
  padding: 10px 0;
  display: flex;
  align-items: center;
  text-align: ${(props) => props.align || 'left'};
  justify-content: ${(props) => (props.align === 'right' ? 'flex-end' : 'flex-start')};

  &:first-child {
    padding-left: 0;
    padding-right: 24px;
  }

  &:not(:first-child):not(:last-child) {
    padding-left: 32px;
    padding-right: 32px;
  }

  &:last-child {
    padding-left: 32px;
    padding-right: 0;
  }

  @media (max-width: 768px) {
    width: 100% !important;
    flex: none !important;
    flex-direction: column;
    align-items: flex-start !important;
    padding: 0 !important;
    text-align: left !important;
    justify-content: flex-start !important;
    gap: 12px;
  }
`;

const UTXOId = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: rgba(16, 41, 0, 0.9);
  word-break: break-all;
  line-height: 1;
`;

const Amount = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #141413;
  line-height: 1;
`;

const OperateButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OperateButton = styled.button`
  width: 36px;
  height: 36px;
  background: #f5f5f5;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #141413;
  font-size: 20px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(243, 245, 242, 0.8);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 20px;
  }
`;

const MobileLabel = styled.div`
  display: none;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: rgba(20, 20, 0, 0.6);
  line-height: 1;

  @media (max-width: 768px) {
    display: block;
  }
`;
